/**
 * @voltxampere/thermal — Per-component thermal computer (Sprint 34-2)
 *
 * Steady-state thermal model: voltage + current measurements →
 * power dissipation → temperature rise via R_thermal table.
 *
 * Formula (per ADR-0056):
 *   P_W       = |V × I|              (instantaneous, abs for reverse)
 *   ΔT_K      = P_W × R_th_CperW     (steady-state, no thermal mass)
 *   T_C       = T_ambient + ΔT_K
 *   severity  = ΔT_K / (T_max - T_ambient)
 *               capped at SEVERITY_DESTRUCTIVE_CAP (=1.5) for stable
 *               rendering. Sprint 35 patlama animation triggers at
 *               severity ≥ 1.2.
 *
 * Time evolution (RC thermal network for cooldown / inrush) Sprint
 * 37+'a defer edildi.
 *
 * Reverse current: P stays positive (heating still occurs — reverse
 * current through a resistor still dissipates I²R).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { getRThermalEntry } from "./r-thermal-table.js";
/** Default ambient temperature when input.ambient_C omitted. */
export const DEFAULT_AMBIENT_C = 25;
/**
 * Severity cap for visualization stability. Beyond T_max the user
 * gets the bright-red destructive halo (Sprint 34-3 overlay) but
 * the numeric severity stops growing so canvas gradient radius +
 * inspector bar fill stay bounded.
 */
export const SEVERITY_DESTRUCTIVE_CAP = 1.5;
/**
 * Compute the thermal frame for a single engine tick.
 *
 * Each measurement contributes one `ComponentThermalState` keyed by
 * its `id`. Components absent from the input array are simply
 * absent from the output map — consumers should treat absence as
 * "cool / no data" rather than "0 °C".
 */
export function computeThermalFrame(input) {
    const ambient = input.ambient_C ?? DEFAULT_AMBIENT_C;
    const components = new Map();
    for (const meas of input.measurements) {
        const entry = getRThermalEntry(meas.type, meas.variant);
        const P_W = Math.abs(meas.voltage * meas.current);
        const deltaT_K = P_W * entry.R_th_CperW;
        const T_C = ambient + deltaT_K;
        // Severity: 0 = at ambient, 1 = at T_max, >1 = destructive.
        // Use entry.T_ambient_C for the normalization range to honour
        // the table's per-entry assumed ambient (consistency with
        // datasheet ratings); falls back gracefully to the shared
        // ambient when entry's T_ambient_C is undefined.
        const range = Math.max(entry.T_max_C - entry.T_ambient_C, 1);
        const rawSeverity = deltaT_K / range;
        const severity = Math.min(rawSeverity, SEVERITY_DESTRUCTIVE_CAP);
        components.set(meas.id, {
            componentId: meas.id,
            type: meas.type,
            P_W,
            deltaT_K,
            T_C,
            T_max_C: entry.T_max_C,
            severity,
        });
    }
    return { t: input.t, components };
}
//# sourceMappingURL=compute.js.map